import { useState, useEffect, lazy, Suspense, Component, type ReactNode } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { ThemeProvider } from './components/shared/ThemeProvider'
import { Preloader } from './components/shared'
import { AppProvider } from './contexts/AppContext'
import { OnboardingV2 } from './components/onboarding/OnboardingV2'
import { PageTransition } from './components/shared/PageTransition'
import { BottomNav } from './components/layout/BottomNav'
import { getData, migrateFromLocalStorage } from './utils/storage'

class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state: { error: Error | null } = { error: null }
  static getDerivedStateFromError(error: Error) { return { error } }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 32, fontFamily: 'monospace', color: '#e00', background: '#1a1a1a', minHeight: '100vh' }}>
          <h2 style={{ color: '#fff' }}>Milo crashed</h2>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>{this.state.error.message}</pre>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: 11, color: '#888', marginTop: 8 }}>{this.state.error.stack}</pre>
          <button onClick={() => { indexedDB.deleteDatabase('keyval-store'); localStorage.clear(); location.reload() }}
            style={{ marginTop: 16, padding: '8px 16px', background: '#333', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
            Reset &amp; Reload
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

const FeedPage = lazy(() => import('./pages/Feed').then(m => ({ default: m.FeedPage })))
const NowPage = lazy(() => import('./pages/Now').then(m => ({ default: m.NowPage })))
const ChatPage = lazy(() => import('./pages/Chat').then(m => ({ default: m.ChatPage })))
const MePage = lazy(() => import('./pages/Me').then(m => ({ default: m.MePage })))
const OrganizeV2Page = lazy(() => import('./pages/OrganizeV2').then(m => ({ default: m.OrganizeV2Page })))
const ProjectPage = lazy(() => import('./pages/Project').then(m => ({ default: m.ProjectPage })))

function AppContent() {
  return (
    <BrowserRouter>
      <Suspense fallback={<Preloader />}>
        <Routes>
          {/* Primary nav tabs */}
          <Route path="/" element={<PageTransition direction="fade"><NowPage /></PageTransition>} />
          <Route path="/feed" element={<PageTransition><FeedPage /></PageTransition>} />
          <Route path="/chat" element={<PageTransition><ChatPage /></PageTransition>} />
          <Route path="/me" element={<PageTransition><MePage /></PageTransition>} />
          {/* Sub-pages */}
          <Route path="/organize" element={<PageTransition><OrganizeV2Page /></PageTransition>} />
          <Route path="/project/:projectId" element={<PageTransition><ProjectPage /></PageTransition>} />
          {/* Legacy redirects → all funnel to new nav structure */}
          <Route path="/now" element={<Navigate to="/" replace />} />
          <Route path="/wellness" element={<Navigate to="/me" replace />} />
          <Route path="/account" element={<Navigate to="/me" replace />} />
          <Route path="/reflections" element={<Navigate to="/me" replace />} />
          <Route path="/settings" element={<Navigate to="/me" replace />} />
        </Routes>
        <BottomNav />
      </Suspense>
    </BrowserRouter>
  )
}

export function App() {
  const [isLoading, setIsLoading] = useState(true)
  const [needsOnboarding, setNeedsOnboarding] = useState(false)

  useEffect(() => {
    let didComplete = false

    async function init() {
      try {
        // Lock to portrait on supported browsers
        try {
          const s = screen as { orientation?: { lock?: (type: string) => Promise<void> } }
          await s.orientation?.lock?.('portrait')
        } catch { /* not supported — ignore */ }

        await migrateFromLocalStorage()
        const data = await getData()

        if (!data.onboarding.completed) {
          setNeedsOnboarding(true)
        }

        if (!didComplete) {
          didComplete = true
          setIsLoading(false)
        }
      } catch (error) {
        console.error('Init error:', error)
        if (!didComplete) {
          didComplete = true
          setIsLoading(false)
        }
      }
    }

    const timeout = setTimeout(() => {
      if (!didComplete) {
        console.warn('Init timeout')
        didComplete = true
        setIsLoading(false)
      }
    }, 5000)

    init()
    return () => clearTimeout(timeout)
  }, [])

  const handleOnboardingComplete = () => {
    setNeedsOnboarding(false)
  }

  if (isLoading) {
    return (
      <ThemeProvider>
        <Preloader />
      </ThemeProvider>
    )
  }

  if (needsOnboarding) {
    return (
      <ThemeProvider>
        <ErrorBoundary>
          <OnboardingV2 onComplete={handleOnboardingComplete} />
        </ErrorBoundary>
      </ThemeProvider>
    )
  }

  return (
    <ThemeProvider>
      <ErrorBoundary>
        <AppProvider initialOnboardingComplete={true}>
          <AppContent />
        </AppProvider>
      </ErrorBoundary>
    </ThemeProvider>
  )
}
